// packages import
import { Box, Checkbox, FormControlLabel, TextField, Tooltip } from '@mui/material'
import { FC, useEffect, useState, useContext } from 'react'
import { useDebounce } from 'use-debounce'

// component imports
import AttorneyFilter from './AttorneyFilter'
import CaseTypeFilter from './CaseTypeFilter'
import LawTypeFilter from './LawTypeFilter'
import StatusFilter from './StatusFilter'
import SentStatusFilter from './SentStatusFilter'

// others
import { AuthContext } from '../../../../context'
import { FinalBillsContext } from '../../../../context/FinalBillsContext'
import { hasAdminAccess } from '../../../../utils'
import BillDeliveryFilter from '../../preBills/filters/BillDeliveryFilter'

const FinalBillsFilters: FC = () => {
  const { isAttorney, currentUser } = useContext(AuthContext)
  const { filters, setFilters } = useContext(FinalBillsContext)
  const canUseTrustMode = hasAdminAccess(currentUser)

  const [searchTerm, setSearchTerm] = useState<string>(filters.searchTerm || '')

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  useEffect(() => {
    if (debouncedSearchTerm === filters.searchTerm) return

    setFilters({ searchTerm: debouncedSearchTerm })

    // Just need the filters value on initial render, if we pass filters.searchTerm as a dependency it will cause a infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, setFilters])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  return (
    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ px: 2, pt: 3, pb: 4 }}>
      {!isAttorney && <AttorneyFilter />}

      <StatusFilter />

      <CaseTypeFilter />

      <LawTypeFilter />

      <BillDeliveryFilter />

      <SentStatusFilter />

      <TextField
        label="Search"
        placeholder="Search by matter # or bill ID"
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ minWidth: 250, ml: 'auto' }}
      />

      {canUseTrustMode && (
        <Tooltip title="Show only bills whose matter currently has trust money available." arrow placement="top">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(filters.onlyWithTrust)}
                onChange={e => setFilters({ onlyWithTrust: e.target.checked })}
              />
            }
            label="Bills with Trust Money"
          />
        </Tooltip>
      )}

      {canUseTrustMode && (
        <Tooltip title="Show only bills whose matter currently has client credit available." arrow placement="top">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(filters.onlyWithCredit)}
                onChange={e => setFilters({ onlyWithCredit: e.target.checked })}
              />
            }
            label="Bills with Client Credit"
          />
        </Tooltip>
      )}
    </Box>
  )
}

export default FinalBillsFilters
